import React from 'react';

import { DotIcon } from '../../../../../components/Icons/DotIcon';
import { DrawerView, ExploreQueryParams } from '../../../../../components/Maps/types/types';
import { Text } from '../../../../../components/Text/Text';
import { trackEvent } from '../../../../../helpers/analytics/trackEvent';
import { EventMykissExploreDrawerMenuClicked } from '../../../../../interfaces/events';
import { useExplore } from '../../../context/ExploreContext';

interface IProps {
  isActive: (mapQuery: ExploreQueryParams) => boolean;
  view: DrawerView;
  icon: React.JSX.Element;
  children: string;
}

const convertToEventTypeProperty = (
  view: DrawerView,
): EventMykissExploreDrawerMenuClicked['properties']['type'] | undefined => {
  switch (view) {
    case 'map-layers':
      return 'map_layers';
    case 'catches':
      return 'catches';
    case 'regulations':
      return 'regulations';
    default:
      return undefined;
  }
};

export const MapDrawerMenuButton = ({ isActive, icon, children, view }: IProps) => {
  const { saveParamsInUrl, exploreQueryParams } = useExplore();
  const { catchFiltersMonths, catchFiltersSpecies } = exploreQueryParams;
  const buttonIsActive = isActive(exploreQueryParams);
  const hasActiveCatchFilters =
    (view === 'catches' && catchFiltersMonths && catchFiltersMonths.length > 0) ||
    (view === 'catches' && catchFiltersSpecies && catchFiltersSpecies.length > 0);

  const onClick = (): void => {
    const trackEventViewProperty = convertToEventTypeProperty(view);

    if (!buttonIsActive && trackEventViewProperty !== undefined) {
      void trackEvent({
        name: 'mykiss_explore_drawer_menu_clicked',
        properties: { type: trackEventViewProperty },
      });
    }

    saveParamsInUrl({ drawerView: view, drawerViewId: undefined });
  };

  return (
    <button
      type="button"
      className={`group mt-2 flex flex-col items-center justify-center sm:mt-0 sm:w-[84px] sm:py-4 ${
        buttonIsActive ? 'bg-lake-fog first:rounded-tl-none md:rounded-l-xxl' : ''
      }`}
      onClick={onClick}
    >
      {hasActiveCatchFilters ? (
        <div className="grid">
          <DotIcon className="z-high col-start-1 row-start-1 ml-6 text-pinktail" />
          <div
            className={`${
              buttonIsActive ? 'text-abyss' : 'text-twilight group-hover:text-midnight'
            } col-start-1 row-start-1`}
          >
            {icon}
          </div>
        </div>
      ) : (
        <div className={buttonIsActive ? 'text-abyss' : 'text-twilight group-hover:text-midnight'}>
          {icon}
        </div>
      )}
      <Text as="span" variant="xs" className="mt-2 grow px-2 text-center sm:font-semibold">
        {children}
      </Text>
    </button>
  );
};
